import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { X, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const hints = new Map([
  [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_39, BarcodeFormat.CODE_128, BarcodeFormat.DATA_MATRIX, BarcodeFormat.QR_CODE, BarcodeFormat.PDF_417]],
  [DecodeHintType.TRY_HARDER, true],
]);

// Labels often prefix the VIN barcode with "I" (import) or add extra chars
export const extractVin = (text) => {
  const clean = (text || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const m = clean.match(/[A-HJ-NPR-Z0-9]{17}/);
  return m ? m[0] : null;
};

export default function VinScanner({ onDetected, onClose }) {
  const videoRef = useRef(null);
  const cb = useRef(onDetected);
  cb.current = onDetected;
  const [error, setError] = useState(null);
  const [hint, setHint] = useState("Point the camera at the VIN barcode (windshield or door jamb).");

  useEffect(() => {
    toast.dismiss();
    const reader = new BrowserMultiFormatReader(hints);
    let controls;
    let done = false;
    // Deferred start so a quick mount/unmount (React dev double-run) never shares the video element
    const timer = setTimeout(() => {
      reader.decodeFromConstraints({ video: { facingMode: { ideal: "environment" } } }, videoRef.current, (result, _err, ctl) => {
        if (!result || done) return;
        const vin = extractVin(result.getText());
        if (vin) { done = true; ctl.stop(); cb.current(vin); }
        else setHint(`Read "${result.getText()}" — not a VIN. Keep aiming at the VIN barcode.`);
      }).then(c => { controls = c; if (done) c.stop(); })
        .catch(() => { if (!done) setError("Camera unavailable. Allow camera access, or use Scan from photo below."); });
    }, 60);
    return () => { done = true; clearTimeout(timer); controls?.stop(); };
  }, []);

  const fromPhoto = async (file) => {
    const url = URL.createObjectURL(file);
    try {
      const res = await new BrowserMultiFormatReader(hints).decodeFromImageUrl(url);
      const vin = extractVin(res.getText());
      if (vin) onDetected(vin); else setError(`Barcode read "${res.getText()}" is not a VIN.`);
    } catch { setError("No barcode found in that photo. Try closer, in focus, with good light."); }
    finally { URL.revokeObjectURL(url); }
  };

  return <div className="fixed inset-0 !mt-0 z-50 bg-black flex flex-col" data-testid="vin-scanner">
    <div className="flex items-center justify-between p-4 text-white">
      <span className="eyebrow text-accent">Scan VIN barcode</span>
      <button onClick={onClose} data-testid="btn-close-scanner" aria-label="close"><X size={26}/></button>
    </div>
    <div className="relative flex-1 flex items-center justify-center overflow-hidden">
      <video ref={videoRef} className="w-full h-full object-cover" muted playsInline/>
      <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-24 border-2 border-accent pointer-events-none"/>
    </div>
    <div className="p-4 space-y-3 text-white">
      <p className="text-sm" data-testid="scanner-hint">{error || hint}</p>
      <label className="btn-amber w-full cursor-pointer" data-testid="btn-scan-photo">
        <ImageIcon size={18} weight="bold"/> Scan from photo
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e)=>e.target.files[0] && fromPhoto(e.target.files[0])}/>
      </label>
    </div>
  </div>;
}
